"use client";

import { Popover } from "@typeform/embed-react";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, memo, useEffect, useMemo, useState } from "react";

import { useResponsive } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { theme } from "shared/lib/styles";
import { parseQueryString } from "shared/lib/utils";

import { safeSetLocalStorage } from "scmp-app/scmp-sdk";

import { TypeformId, TypeformIsShownKey } from "./consts";

const customCss = `
  .tf-v1-popover-button {
    background-color: #ffcb05 !important;
    svg {
      path {
        fill: #001246;
      }
    }
  }
`;

export const TypeformComponent: FunctionComponent = () => {
  const { user } = useAtomValue(accountAtom);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { query } = useRouter();
  const searchQuery = useMemo(() => parseQueryString(query.q) ?? "", [query.q]);

  const [isShowPopover, setIsShowPopover] = useState(false);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

  useEffect(() => {
    if (localStorage?.getItem(TypeformIsShownKey) === "1") return;

    setIsShowPopover(true);

    // Auto open after 3 seconds for desktop
    if (!isMobile) {
      const timer = setTimeout(() => {
        setShouldAutoOpen(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const handleClick = () => {
    safeSetLocalStorage(TypeformIsShownKey, "1");
  };

  const hiddenFields = useMemo(
    () => ({
      utm_content: searchQuery,
      utm_source: isMobile ? "mdot" : "desk",
      utm_term: user?.uuid ?? "",
    }),
    [isMobile, searchQuery, user?.uuid],
  );

  const handleSurveyFinished = () => {
    setIsShowPopover(false);
  };

  if (!isShowPopover) return null;
  return (
    <>
      <style>{customCss}</style>
      <Popover
        height={500}
        hidden={hiddenFields}
        hideHeaders
        id={TypeformId}
        medium="popover"
        onClose={handleSurveyFinished}
        onReady={handleClick}
        onSubmit={handleSurveyFinished}
        open={shouldAutoOpen ? "load" : undefined}
      />
    </>
  );
};

export const Typeform = memo(TypeformComponent);
